<template>
    <div class="DatePicker" :class="{ 'DatePicker--disabled': disabled, 'DatePicker--enabledClear': enableClear }">
        <div class="DatePicker__control">
            <VueDatePicker
                ref="datepicker"
                :class="{'DatePicker__picker--hidden': hidePicker}"
                :model-value="currentValue"
                :model-type="MODEL_FORMAT"
                :formats="{ input: displayFormat }"
                :time-config="{ enableTimePicker: false }"
                :disabled="disabled"
                :min-date="currentMinDate"
                :max-date="currentMaxDate"
                :week-start="WeekStart.Sunday"
                :placeholder="placeholder"
                :ui="{ input: 'DatePicker__input' }"
                :input-attrs="{ clearable: false, hideInputIcon: true }"
                text-input
                auto-apply
                teleport
                @update:model-value="onChange"
            />
            <div
                v-if="showInvalidDate && invalidValue"
                class="DatePicker__invalidDate"
                @click="openCalendar"
            >
                <span>
                    {{ value }}
                </span>
                <div v-if="enableClear && value" class="DatePicker__invalidDateClear" @click="clear">
                    <FontAwesomeIcon :icon="faTimes" />
                </div>
            </div>
        </div>
        <div v-if="enableClear && currentValue" ref="clearIcon" class="DatePicker__clearIcon" @click="clear">
            <FontAwesomeIcon :icon="faTimes" />
        </div>
        <div class="DatePicker__icon">
            <FontAwesomeIcon :icon="faCalendarAlt" />
        </div>
    </div>
</template>

<style lang="scss">
    // stylelint-disable selector-max-combinators, selector-max-compound-selectors
    @import "~@vuepic/vue-datepicker/dist/main.css";

    $datepicker-theme-color: #2ab496;
    $datepicker-input-padding-horizontal: 10px;
    $datepicker-clear-icon-margin: 5px;
    $datepicker-icon-color: #2b2b2b;
    $datepicker-clear-icon-color: #788ea1;
    $datepicker-input-border-color: #e7e6e6;
    $datepicker-input-background-color: #f9fafb;
    $datepicker-disabled-icon-color: #bbb;
    $datepicker-disabled-input-background-color: #f3f3f3;
    $datepicker-disabled-input-border-color: $datepicker-input-border-color;

    .DatePicker {
        position: relative;

        // PRODUCT-26110 Task 12b (vue-datetime -> @vuepic/vue-datepicker): the new
        // library themes itself via CSS custom properties instead of deeply-nested
        // class selectors -- much simpler than vue-datetime's `.vdatetime-*` overrides.
        --dp-primary-color: #{$datepicker-theme-color};

        & &__picker--hidden input {
            // stylelint-disable-next-line declaration-no-important
            display: none !important;
        }

        &__icon {
            position: absolute;
            pointer-events: none;
            right: $datepicker-input-padding-horizontal;
            top: 50%;
            transform: translateY(-50%);
            color: $datepicker-icon-color;
        }

        &__clearIcon {
            display: none;
            position: absolute;
            cursor: pointer;
            top: 50%;
            transform: translateY(-50%) translateX($datepicker-input-padding-horizontal + $datepicker-clear-icon-margin);
            color: $datepicker-clear-icon-color;
            width: 13px;
            text-align: center;
        }

        &__invalidDateClear {
            display: none;
            cursor: pointer;
            margin-left: $datepicker-clear-icon-margin;
            color: $datepicker-clear-icon-color;
            width: 13px;
            text-align: center;
        }

        &__input, &__invalidDate {
            display: block;
            width: 100%;
            border: 1px solid $datepicker-input-border-color;
            height: 2.5em;
            border-radius: 5px;
            outline: none;
            padding: 0 $datepicker-input-padding-horizontal;
            background: $datepicker-input-background-color;
            font: inherit;
            cursor: pointer;
        }

        &__invalidDate {
            display: flex;
            align-items: center;
        }

        &--enabledClear:hover &__clearIcon, &--enabledClear:hover &__invalidDateClear {
            display: block;
        }

        &--disabled &__input {
            background: $datepicker-disabled-input-background-color;
            border-color: $datepicker-disabled-input-border-color;
            cursor: not-allowed;
        }

        &--disabled > &__icon {
            color: $datepicker-disabled-icon-color;
        }
    }
</style>

<script lang="ts">
    import { DateTime } from 'luxon';
    import { Component, Prop, Watch, Vue } from 'vue-property-decorator';

    import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
    import { faTimes } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { VueDatePicker, WeekStart } from '@vuepic/vue-datepicker';

    // The shape `currentValue` is exchanged in. A bare calendar date keeps VueDatePicker on
    // date-fns parsing and formatting, which work in the browser's own timezone. The `iso`
    // model type instead round-trips through `new Date(string)`, and a date with no time in
    // it is midnight UTC there -- the day before, for every viewer behind UTC.
    const MODEL_FORMAT = 'yyyy-MM-dd';

    // VueDatePicker reads its bounds with `new Date(...)`, which has that same UTC-midnight
    // reading of a bare date. Spelling out a time with no zone pins them to midnight in the
    // browser's own timezone.
    function atLocalMidnight(date: string): string | null {
        return date ? `${date}T00:00:00` : null;
    }

    @Component({
        emits: [ 'input' ],
        components: { FontAwesomeIcon, VueDatePicker },
    })
    export default class DatePicker extends Vue {
        private faCalendarAlt = faCalendarAlt;
        private faTimes = faTimes;
        private WeekStart = WeekStart;
        private MODEL_FORMAT = MODEL_FORMAT;

        // string must matches valueFormat Prop, default is ISO
        @Prop({ default: null })
        private value!: Date | string | null;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Boolean, default: false })
        private showInvalidDate!: boolean;

        @Prop({ type: Boolean, default: false })
        private enableClear!: boolean;

        @Prop({ default: null })
        private minDate!: Date | string | null;

        @Prop({ default: null })
        private maxDate!: Date | string | null;

        @Prop({ type: String, default: '' })
        private placeholder!: string;

        // dates use luxon format
        @Prop({ type: String, default: 'yyyy-MM-dd' })
        private valueFormat!: string;

        @Prop({ type: String, default: 'M/d/yyyy' })
        private displayFormat!: string;

        // `currentValue` is always kept in plain calendar-date form (`MODEL_FORMAT`)
        // regardless of what external `valueFormat` the caller requested -- `valueFormat`
        // can be an arbitrary, non-ISO-parseable format (see JsonSchemaDateForm.vue/Date.vue,
        // which forward a schema-driven format), so the internal representation needs to stay
        // a format both `VueDatePicker` and `formatDate()`'s luxon calls can always round-trip.
        // These are initialised, not just declared: the decorator builds `data` from the
        // properties an instance actually carries after construction, and a field with no
        // initializer emits no code -- it would stay outside reactivity, so a picked date
        // would never reach the input.
        private currentValue: string | null = null;
        private currentMinDate: string | null = null;
        private currentMaxDate: string | null = null;

        @Watch('value', { immediate: true })
        private async onExternalValueChange(value: Date | string | null): Promise<void> {
            this.currentValue = this.formatDate(value);
            // The clear icon is positioned off a ref that only exists once the new value
            // has rendered.
            await this.$nextTick();
            this.positionClearIcon();
        }

        @Watch('minDate', { immediate: true })
        private onExternalMinDateChange(minDate: Date | string | null): void {
            this.currentMinDate = atLocalMidnight(this.formatDate(minDate));
        }

        @Watch('maxDate', { immediate: true })
        private onExternalMaxDateChange(maxDate: Date | string | null): void {
            this.currentMaxDate = atLocalMidnight(this.formatDate(maxDate));
        }

        private mounted(): void {
            this.positionClearIcon();
        }

        private openCalendar(): void {
            const dateTimeComp = (this.$refs.datepicker || {}) as any;
            if (dateTimeComp.openMenu) {
                dateTimeComp.openMenu();
            }
        }

        private positionClearIcon(): void {
            if (this.$refs.clearIcon) {
                const currentValue = this.currentValue;
                const displayDate = currentValue && DateTime.fromISO(currentValue).toFormat(this.displayFormat);
                (this.$refs.clearIcon as HTMLElement).style.left = `${displayDate?.length || 0}ch`;
            }
        }

        private get hidePicker(): boolean {
            return this.showInvalidDate ? this.invalidValue : false;
        }

        private get invalidValue(): boolean {
            return Boolean(this.value) && !this.currentValue;
        }

        // `@vuepic/vue-datepicker` hands us the picked day already formatted as
        // `MODEL_FORMAT`, so this only has to convert to the external `valueFormat`.
        //
        // This is deliberately a ONE-WAY `:model-value=` binding + a single
        // `@update:model-value=` listener, NOT `v-model=`. Combining
        // `v-model` with an explicit listener for the same event on one element compiles
        // both into one handler array (v-model's own setter runs first), so by the time
        // this method ran, `this.currentValue` had already been overwritten to equal
        // `isoDate` -- the `isoDate !== this.currentValue` guard below was then always
        // false and the external `input` event never fired for ANY picked date. This
        // method is now the ONLY writer of `currentValue` on user interaction.
        private async onChange(isoDate: string | null): Promise<void> {
            if (isoDate === this.currentValue) {
                this.positionClearIcon();
                return;
            }
            const outputDate = isoDate ? DateTime.fromISO(isoDate).toFormat(this.valueFormat) : null;
            this.currentValue = isoDate;
            this.$emit('input', outputDate);
            // This assignment is what creates the clear icon, so its ref lands a tick later.
            await this.$nextTick();
            this.positionClearIcon();
        }

        private clear(event: MouseEvent): void {
            event.stopPropagation();
            this.currentValue = null;
            this.$emit('input', null);
        }

        private formatDate(date: Date | string | null): string {
            if (!date) {
                return '';
            }
            const finalDate = date instanceof Date
                ? DateTime.fromJSDate(date)
                : /^\d{4}-\d{2}-\d{2}(T\d{1,2}:\d{2}:\d{2}\.\d{3}Z)?$/.test(date)
                    ? DateTime.fromISO(date)
                    : DateTime.fromFormat(date, this.valueFormat);
            return finalDate.toISODate();
        }
    }
</script>
